import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { GetUserSubscriptionsResponse, growthSdk } from '@fff-web/growth-sdk';
import GlobalNavInternalContext from '../context/GlobalNavInternalContext';

export type SubscriptionStatus = 'EXPIRED' | 'ACTIVE' | 'CANCELED';

export const useUserSubscriptions = () => {
  const { env } = useContext(GlobalNavInternalContext);

  const growthSdkInstance = useMemo(() => {
    return growthSdk(env.REACT_APP_ENVIRONMENT as Parameters<typeof growthSdk>[0], true);
  }, [env.REACT_APP_ENVIRONMENT]);

  const [clubMembershipSubscription, setClubMembershipSubscription] =
    useState<GetUserSubscriptionsResponse['subscriptions'][number]>();

  const [boxSubscriptions, setBoxSubscriptions] =
    useState<GetUserSubscriptionsResponse['subscriptions'][number][]>();

  // Member tenure: earliest `startAt` across ALL subscriptions, regardless of type or status.
  // Derived from the unfiltered response so it stays stable across cancel/resubscribe (matching the
  // legacy account-level `activationDate`) and counts add-on-first members, which the `boxSubscriptions`
  // type/active filtering below would otherwise exclude.
  const [memberSince, setMemberSince] = useState<string>();

  const fetchUserSubscriptions = useCallback(async () => {
    // `unCurlEnabled: true` is required — box subscriptions are otherwise feature-flag-gated by the
    // UnCurly migration and omitted from the response, which drops the tenure-bearing box (and every
    // box-derived field below). Mirrors the asado BFF callers of this endpoint.
    const response = await growthSdkInstance.getUserSubscriptions(undefined, {
      unCurlEnabled: true,
    });

    if (!response.ok) return;

    const earliestStartAt = response.data.subscriptions
      .map((sub) => sub.startAt)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))[0];
    if (earliestStartAt) setMemberSince(earliestStartAt);

    const clubMembershipSub = response.data.subscriptions.find((sub) => sub.type === 'MEMBERSHIP');

    // TODO: refactor properly to handle case for multiple BOX subscriptions
    const allBoxSubs = response.data.subscriptions.filter((sub) => sub.type === 'BOX');
    // Need to select the active one if it exists because a user can have multiple BOX subscriptions with various statuses
    const activeBoxSubs = allBoxSubs.filter((sub) => sub.status === 'ACTIVE');
    const boxSubs = activeBoxSubs?.length
      ? activeBoxSubs
      : allBoxSubs.length
        ? allBoxSubs
        : undefined;

    if (!clubMembershipSub && !boxSubs) return;

    if (clubMembershipSub) {
      setClubMembershipSubscription({
        ...clubMembershipSub,
        status: (clubMembershipSub.status === 'NON_RENEWING'
          ? 'CANCELED'
          : clubMembershipSub.status) as SubscriptionStatus,
      });
    }

    if (boxSubs) {
      setBoxSubscriptions(
        boxSubs.map((sub) => ({
          ...sub,
          status: (sub.status === 'NON_RENEWING' ? 'CANCELED' : sub.status) as SubscriptionStatus,
        })),
      );
    }
  }, [growthSdkInstance]);

  useEffect(() => {
    fetchUserSubscriptions();
  }, [fetchUserSubscriptions]);

  return {
    clubMembershipSubscription,
    boxSubscriptions,
    memberSince,
    refetch: fetchUserSubscriptions,
  };
};
